/**
 * useVoicePractice Hook
 *
 * Manages voice recording, transcription, and scoring for the practice flow.
 * Handles MediaRecorder for audio capture and API calls for processing.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectCentralAccessToken } from '../store/slices/centralAuthSlice';
import { ATTEMPT_ERROR_COPY } from '../utils/attemptErrors';

/** The API base every RTK slice uses (store/api.ts). */
export const VOICE_API_BASE = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/+$/, '');

// ============================================
// Types
// ============================================

export type VoicePracticeStatus = 'idle' | 'recording' | 'processing' | 'error' | 'complete';

/**
 * Dimension detail of a recording analysis. No `overall`: the legacy heuristic
 * overall the server still sends (deprecated, for the mobile contract) is never
 * read here; the recording's overall is `RecordingEvidence.overall`.
 */
export interface VoiceScores {
  clarity?: number;
  pace?: number;
  confidence?: number;
  energy?: number;
  structure?: number;
  delivery?: number;
  domains?: Record<string, number>;
}

/**
 * The recording's ONE overall: the canonical evidence its finalize records
 * (the same deterministic delivery profile, computed by the server with no
 * provider call). `preliminary` until the attempt is finalized. null overall =
 * not measured, never 0.
 */
export interface RecordingEvidence {
  overall: number | null;
  dimensions: Record<string, number>;
  modality: 'audio';
  profile?: string;
  profileVersion?: string;
  preliminary?: boolean;
}

export interface VoiceAnalysis {
  wordCount: number;
  /** Null when no rate was measured (no speech, or no measured duration). */
  wordsPerMinute: number | null;
  fillerWordCount: number | null;
  fillerWords: string[];
  durationSeconds: number | null;
}

/** 'model' = written by the feedback LLM; 'rule_based_fallback' = templated tips. */
export type FeedbackSource = 'model' | 'rule_based_fallback';

/** Shown when the server found no speech: nothing was scored (RC10). */
export const NO_SPEECH_MESSAGE = 'No speech was detected, so nothing was scored. Please record again.';

export interface VoicePracticeState {
  status: VoicePracticeStatus;
  transcript: string | null;
  feedbackText: string | null;
  feedbackSource: FeedbackSource | null;
  scores: VoiceScores | null;
  /** The recording's overall (canonical evidence); null before analysis or when nothing was said. */
  evidence: RecordingEvidence | null;
  analysis: VoiceAnalysis | null;
  isSessionComplete: boolean;
  nextPrompt: string | null;
  error: string | null;
  /** The server's error code for a refused upload (e.g. 402 USAGE_BLOCKED), so the page can word a limit as a limit. */
  errorCode: string | null;
  recordingDuration: number;
  audioBlob: Blob | null;
}

export interface UseVoicePracticeOptions {
  /** A session whose turns go to POST /voice/practice/sessions/:id/turn (AI coach). */
  sessionId?: string | null;
  /**
   * The practice session (training, NPC) a quick analysis belongs to. Sent as
   * the `sessionId` field of POST /voice/practice/analyze, where the server
   * refuses it before any provider work unless it is the caller's open session.
   */
  analysisSessionId?: string | null;
  /**
   * This recording belongs to a practice session (training, NPC, AI coach). If
   * no session was created — the start was refused — the recording is never
   * uploaded or analyzed.
   */
  requireSession?: boolean;
  onTranscriptReady?: (transcript: string) => void;
  onScoresReady?: (scores: VoiceScores) => void;
  onError?: (error: string) => void;
}

export interface UseVoicePracticeReturn extends VoicePracticeState {
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  reset: () => void;
  isSupported: boolean;
}

// ============================================
// API Helper
// ============================================

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

export interface VoiceTurnResponse {
  /** 'no_speech' when nothing was said: no scores, nothing stored or charged. */
  outcome?: 'no_speech';
  feedbackSource?: FeedbackSource;
  sessionId: string;
  transcript: string;
  feedbackText: string;
  scores: VoiceScores;
  evidence?: RecordingEvidence | null;
  isSessionComplete: boolean;
  nextPrompt: string | null;
  aiAudioUrl: string | null;
  analysis: VoiceAnalysis;
}

export interface QuickAnalyzeResponse {
  outcome?: 'no_speech';
  feedbackSource?: FeedbackSource;
  transcript: string;
  feedbackText: string;
  scores: VoiceScores;
  evidence?: RecordingEvidence | null;
  analysis: VoiceAnalysis;
}

export interface RecordingUploadOptions {
  turnSessionId: string | null;
  analysisSessionId: string | null;
  requireSession: boolean;
  token: string | null;
}

/** A refused upload: the server's readable message, with its status and stable code. */
export class RecordingUploadError extends Error {
  constructor(message: string, readonly status: number, readonly code: string | null) {
    super(message);
    this.name = 'RecordingUploadError';
  }
}

/** Codes that are a usage or safety LIMIT — not a recording or browser problem. */
export const RECORDING_LIMIT_CODES: ReadonlySet<string> = new Set(['USAGE_BLOCKED', 'CONVERSATION_SAFETY_LIMIT']);

/**
 * Codes for a recording the SERVICE could not admit (503: usage could not be
 * checked) — nothing was processed or counted, and the user's microphone,
 * browser or device played no part, so it is never shown as a recording error.
 */
export const RECORDING_SERVICE_UNAVAILABLE_CODES: ReadonlySet<string> = new Set(['USAGE_AUTHORITY_UNAVAILABLE']);

/** A server refusal shown in its own notice, never under "Recording Error" (microphone/browser advice). */
export function isRecordingRefusalNotice(code: string | null): boolean {
  return !!code && (RECORDING_LIMIT_CODES.has(code) || RECORDING_SERVICE_UNAVAILABLE_CODES.has(code));
}

/** Thrown when a recording that needs a practice session has none: nothing is sent. */
export const NO_SESSION_MESSAGE =
  'This practice session was not started, so the recording was not uploaded or analyzed.';

/**
 * Upload one recording for transcription and scoring — or, when it needs a
 * practice session and has none (session creation was refused), refuse before
 * any request is made.
 */
export async function submitRecording(
  audioBlob: Blob,
  { turnSessionId, analysisSessionId, requireSession, token }: RecordingUploadOptions,
  fetchImpl: typeof fetch = fetch
): Promise<VoiceTurnResponse | QuickAnalyzeResponse> {
  if (requireSession && !turnSessionId && !analysisSessionId) {
    throw new Error(NO_SESSION_MESSAGE);
  }

  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  if (!turnSessionId && analysisSessionId) {
    formData.append('sessionId', analysisSessionId);
  }

  // Choose endpoint based on whether we have a turn session. Same API base as
  // every RTK slice (it was hard-coded to the page origin's /api/v1, so a
  // build whose API is on another origin uploaded to the web host).
  const endpoint = turnSessionId
    ? `${VOICE_API_BASE}/voice/practice/sessions/${turnSessionId}/turn`
    : `${VOICE_API_BASE}/voice/practice/analyze`;

  // Build headers with auth token from Redux (memory-only, no localStorage)
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetchImpl(endpoint, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // ApiResponse.error sends { message, error: { code, details } }: the
    // readable text (e.g. a voice-analysis limit, worded by the server) is
    // `message`. `error` is an object there, and only a string in older shapes.
    const code =
      errorData.error && typeof errorData.error === 'object' && typeof errorData.error.code === 'string'
        ? (errorData.error.code as string)
        : null;
    const text =
      (typeof errorData.message === 'string' && errorData.message) ||
      (typeof errorData.error === 'string' && errorData.error) ||
      (code && ATTEMPT_ERROR_COPY[code]?.message) ||
      `Request failed: ${response.status}`;
    throw new RecordingUploadError(text, response.status, code);
  }

  const result = (await response.json()) as ApiResponse<VoiceTurnResponse | QuickAnalyzeResponse>;

  if (!result.success) {
    throw new Error(result.error || 'Unknown error');
  }

  return result.data;
}

// ============================================
// Hook Implementation
// ============================================

const initialState: VoicePracticeState = {
  status: 'idle',
  transcript: null,
  feedbackText: null,
  feedbackSource: null,
  scores: null,
  evidence: null,
  analysis: null,
  isSessionComplete: false,
  nextPrompt: null,
  error: null,
  errorCode: null,
  recordingDuration: 0,
  audioBlob: null,
};

export function useVoicePractice(options: UseVoicePracticeOptions = {}): UseVoicePracticeReturn {
  const { sessionId, analysisSessionId, requireSession = false, onTranscriptReady, onScoresReady, onError } = options;

  // Central access token from Redux (memory-only, no localStorage)
  const centralToken = useSelector(selectCentralAccessToken);

  const [state, setState] = useState<VoicePracticeState>(initialState);

  // Refs for recording
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // Check if MediaRecorder is supported
  const isSupported = typeof window !== 'undefined' && 'MediaRecorder' in window;

  /**
   * Start recording audio from the microphone
   */
  const startRecording = useCallback(async () => {
    if (!isSupported) {
      const errorMsg = 'Audio recording is not supported in this browser';
      setState((prev) => ({ ...prev, status: 'error', error: errorMsg, errorCode: null })); // not a limit refusal: no limit notice
      onError?.(errorMsg);
      return;
    }

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      streamRef.current = stream;
      audioChunksRef.current = [];

      // Determine the best supported mime type
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
      ];
      const mimeType = mimeTypes.find((type) => MediaRecorder.isTypeSupported(type)) || 'audio/webm';

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        const errorMsg = 'Recording error occurred';
        setState((prev) => ({ ...prev, status: 'error', error: errorMsg, errorCode: null })); // not a limit refusal: no limit notice
        onError?.(errorMsg);
      };

      mediaRecorderRef.current = mediaRecorder;

      // Start recording
      mediaRecorder.start(1000); // Collect data every second
      startTimeRef.current = Date.now();

      // Start duration timer
      timerRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setState((prev) => ({ ...prev, recordingDuration: duration }));
        }
      }, 1000);

      setState((prev) => ({
        ...prev,
        status: 'recording',
        error: null,
        errorCode: null,
        transcript: null,
        feedbackText: null,
        feedbackSource: null,
        scores: null,
        evidence: null,
        analysis: null,
        recordingDuration: 0,
      }));
    } catch (error) {
      console.error('Failed to start recording:', error);
      const errorMsg =
        error instanceof Error
          ? error.message.includes('Permission')
            ? 'Microphone permission denied. Please allow access.'
            : error.message
          : 'Failed to start recording';
      setState((prev) => ({ ...prev, status: 'error', error: errorMsg, errorCode: null })); // not a limit refusal: no limit notice
      onError?.(errorMsg);
    }
  }, [isSupported, onError]);

  /**
   * Stop recording and process the audio
   */
  const stopRecording = useCallback(async () => {
    // Stop the timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Stop the MediaRecorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      return new Promise<void>((resolve) => {
        const recorder = mediaRecorderRef.current!;

        recorder.onstop = async () => {
          // Stop all tracks
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
          }

          // Create audio blob
          const audioBlob = new Blob(audioChunksRef.current, {
            type: recorder.mimeType || 'audio/webm',
          });
          audioChunksRef.current = [];

          // Update state to processing
          setState((prev) => ({
            ...prev,
            status: 'processing',
            audioBlob,
          }));

          // Send to API
          try {
            const result = await submitRecording(audioBlob, {
              turnSessionId: sessionId || null,
              analysisSessionId: analysisSessionId || null,
              requireSession,
              token: centralToken,
            });

            // No speech: an explicit outcome, never a "complete" analysis of zeros.
            if (result.outcome === 'no_speech') {
              setState((prev) => ({ ...prev, status: 'error', error: NO_SPEECH_MESSAGE }));
              onError?.(NO_SPEECH_MESSAGE);
              resolve();
              return;
            }

            // Determine if this is a full turn response or quick analyze
            const isFullTurn = 'sessionId' in result;

            setState((prev) => ({
              ...prev,
              status: 'complete',
              transcript: result.transcript,
              feedbackText: result.feedbackText,
              feedbackSource: result.feedbackSource ?? null,
              scores: result.scores,
              evidence: result.evidence ?? null,
              analysis: result.analysis,
              isSessionComplete: isFullTurn ? (result as VoiceTurnResponse).isSessionComplete : false,
              nextPrompt: isFullTurn ? (result as VoiceTurnResponse).nextPrompt : null,
            }));

            // Callbacks
            onTranscriptReady?.(result.transcript);
            onScoresReady?.(result.scores);
          } catch (error) {
            console.error('Failed to process audio:', error);
            const errorMsg = error instanceof Error ? error.message : 'Failed to process audio';
            setState((prev) => ({
              ...prev,
              status: 'error',
              error: errorMsg,
              errorCode: error instanceof RecordingUploadError ? error.code : null,
            }));
            onError?.(errorMsg);
          }

          resolve();
        };

        recorder.stop();
      });
    }
  }, [sessionId, analysisSessionId, requireSession, centralToken, onTranscriptReady, onScoresReady, onError]);

  /**
   * Reset the hook state
   */
  const reset = useCallback(() => {
    // Stop any ongoing recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setState(initialState);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  return {
    ...state,
    startRecording,
    stopRecording,
    reset,
    isSupported,
  };
}

export default useVoicePractice;
